/**
 * What an `operators[]` entry COSTS US, for display.
 *
 * THE RULE IS NOT HERE, AND MUST NOT BE COPIED HERE. The API resolves an
 * operator's cost across its three layers — the `kind:'operator'` ledger
 * line, the entry itself, then the legacy `operator_*` scalars (the last
 * only for the booking's lead operator) — in
 *   pickuphungary-dashboard-api/src/packages/ph-core/src/Helpers/
 *   operatorCost.helper.ts
 * and ships the answer on each entry as `resolved_cost`. This file only
 * decides how to RENDER what the server already worked out. Read that
 * helper's docblock before changing anything in this one; it exists because
 * seven call sites each had their own slightly different version of the
 * chain, and a copy in the dashboard would be the eighth.
 *
 * WHY NOT JUST READ `op.price`? Because `operators[].price` is one of the
 * three layers, not the answer, and on real bookings it is the empty one:
 *
 *   - Approving a driver's expense receipt appends a cost operator to
 *     `operators[]` with NO price on purpose, so the same money is not owed
 *     twice. The array is then non-empty and partly unpriced.
 *   - The b76cf934 ledger fix writes the lead operator's fee to a
 *     `kind:'operator'` line and leaves the entry unpriced.
 *
 * Measured in production on 2026-11-01: of 741 bookings with a non-empty
 * `operators[]`, 66 had at least one entry whose `price` was absent while
 * the ledger line or the legacy mirror held the real figure. All 66 rendered
 * "Price: N/A" and "Total Operators Price: 0 EUR" on Order Details, which is
 * the bug this file was added to end (Trello oDyHPg0k). PU_DASH_3730531 is
 * the worked example: entry unpriced, `kind:'operator'` line 6 000 HUF.
 *
 * So: a reader that says `op.price` is wrong on 9% of multi-operator
 * bookings, and it fails SILENTLY — it renders a plausible "N/A" rather than
 * an error. If you are tempted to simplify these call sites back to
 * `op.price`, that is the regression, and
 * `src/Pages/Orders/Components/__tests__/OrderDetailsModal.operatorPrice.test.jsx`
 * is what will stop you.
 */

import thousandSeparator from './numberFormat';

/**
 * The entry's resolved cost, or null when the server sent none.
 *
 * Null — and therefore "N/A" at the call sites — in two cases that look the
 * same here but are NOT the same thing:
 *   - an unassigned slot, which has no `operator_id` to resolve against; and
 *   - a cost bucket with no agreed price, which is the normal, correct state
 *     for an expense account (its money is an `adjustment` line, shown in the
 *     Order Ledger tab, deliberately excluded from "what was this operator
 *     assigned" — see `getOperatorLines`).
 * `resolved_cost.priceSource === 'none'` distinguishes them if a screen ever
 * needs to; no screen does yet, so this does not invent a label for it.
 *
 * DEPLOY SKEW: when `resolved_cost` is ABSENT ENTIRELY — an API older than
 * the field, or a rolled-back one — this falls back to the entry's own
 * `price`/`currency`. That is NOT a second copy of the three-layer rule and
 * must not grow into one: it is the single layer the dashboard can see for
 * itself, and its only job is to make a UI deployed ahead of the API render
 * exactly as it did before this fix instead of regressing to "N/A" the 667
 * of 741 bookings whose entry IS priced. (667 + the 66 this fixes is 733;
 * the remaining 8 have no operator cost in any layer and correctly show
 * nothing either way.) Resolving the other two layers
 * here would be the duplication this file exists to prevent — the ledger
 * line is not in this payload and the lead rule is not reimplementable
 * safely (a naive `|| order.operator_price` hands the driver's fee to every
 * other operator on the booking, which is the bug the API helper was
 * extracted to kill).
 *
 * `resolved_cost` PRESENT but its `price` null means the server looked and
 * found nothing — that is an answer, and it is respected rather than
 * second-guessed from the entry.
 */
export function operatorEntryPrice(op) {
  const resolved = op?.resolved_cost;
  const amount = resolved ? resolved.price : op?.price;
  if (amount == null || Number.isNaN(Number(amount))) return null;

  const currency = resolved ? resolved.currency : op?.currency;

  return { amount: Number(amount), currency: currency || null };
}

/**
 * One entry's price as the panel shows it, or null for "nothing to show".
 *
 * The currency is whatever layer won, and is NOT defaulted to EUR. The old
 * call site rendered `op.currency || 'EUR'`, which labelled this booking's
 * 6 000 HUF as "6,000 EUR" — a 400x overstatement of what we owe, in the
 * one place the office checks it. An amount whose currency we do not know is
 * shown bare rather than relabelled into a currency it is not.
 */
export function formatOperatorEntryPrice(op) {
  const resolved = operatorEntryPrice(op);
  if (!resolved) return null;

  return [thousandSeparator(resolved.amount), resolved.currency]
    .filter(Boolean)
    .join(' ');
}

/**
 * The cash this operator already took off the customer, resolved the same
 * way and for the same reason — it is a NEGATIVE cost, so hiding it
 * overstates what we owe.
 *
 * Fixed alongside the price because it is the same defect in the adjacent
 * row, not because it was reported: the panel gated the row on
 * `op.cash_from_client > 0`, the entry layer alone. Only 2 entries in
 * production were affected on 2026-11-01 (PU_DASH_2314257, 20; and
 * PU_DASH_8020117, 58 — both from the legacy mirror), which is small, but
 * resolving the price and not the cash beside it would leave the next reader
 * unable to tell which rows had been migrated and which had merely not
 * failed yet.
 *
 * Returns null when there is no cash to show, so the caller drops the row —
 * the panel has always hidden it rather than printing "0", and a zero here
 * is genuinely "took no cash" rather than missing data.
 */
export function formatOperatorEntryCash(op) {
  const resolved = op?.resolved_cost;
  // Same deploy-skew fallback as the price side, for the same reason.
  const amount = resolved ? resolved.cashFromClient : op?.cash_from_client;
  if (amount == null || Number.isNaN(Number(amount)) || Number(amount) <= 0) return null;

  const currency = resolved
    ? resolved.cashFromClientCurrency
    : op?.cash_from_client_currency;

  return [thousandSeparator(Number(amount)), currency].filter(Boolean).join(' ');
}

/**
 * What was booked against this operator AFTER its fee was agreed — a
 * parking receipt, a waiting credit — as its own "Adjustments" row.
 *
 * A SECOND FIGURE, NEVER FOLDED INTO THE PRICE. "Price" is the fee the
 * operator was ASSIGNED — `findOperatorLine` excludes `kind:'adjustment'`
 * deliberately, and `resolved_cost.price` inherits that. These are the
 * other question: what was booked afterwards. The office asked to SEE
 * adjustments, not to have them silently merged into the fee, and merging
 * them would also destroy the only thing that currently distinguishes an
 * operator with an expensive job from one with a cheap job and a large
 * receipt. See `operatorPriceTotals` for which screens add these two
 * together and which do not — they genuinely differ, and that docblock is
 * the checked list.
 *
 * This is the only place a cost operator's money shows up on this panel: an
 * expense bucket is appended with no fee at all, so without this row its
 * card reads "Price: N/A" and nothing else, which is how PU_DASH_3730531
 * looked while really carrying 4 450 HUF of parking.
 *
 * Returns null when there is nothing to show, so the row is omitted rather
 * than printed empty — 639 of the 741 production bookings with operators
 * have no adjustment at all (2026-11-01), and a zero row on all of them
 * would be noise. A reversal pair that nets to zero is also nothing: the
 * API drops it before this sees it.
 *
 * No deploy-skew fallback, deliberately, unlike the price and cash above.
 * Those degrade to a field the dashboard can see for itself; adjustments
 * live only in `order_lines`, which this payload does not carry. An older
 * API simply shows no row — the behaviour before this existed.
 */
export function formatOperatorAdjustments(op) {
  const rows = op?.resolved_cost?.adjustments;
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return rows
    .map(({ amount, currency }) => [thousandSeparator(amount), currency].filter(Boolean).join(' '))
    .join(' · ');
}

/**
 * The booking's operator cost, summed PER CURRENCY.
 *
 * NEVER ONE SCALAR. Operators on one booking can be priced in different
 * currencies — 8 of those 741 production bookings resolve to costs in more
 * than one — so a single number is not merely mislabelled, there is no
 * correct currency to label it with. This mirrors the shape the API's
 * `cost_breakdown` uses (`StatsController.operatorCostBreakdown`, grouped per
 * charge AND per currency) and the one the Orders table's operator-price
 * column already used, rather than inventing a third.
 *
 * ADJUSTMENTS ARE NOT IN THIS TOTAL, and that is a decision, not an
 * oversight. Checked rather than assumed, 2026-11-01, because the estate is
 * NOT of one mind about this and it is easy to assert the wrong thing:
 *
 *   - Month End's operator ledger and `StatsController.operatorBookingTotals`
 *     (the operator payment screen) DO include operator-side adjustments —
 *     they answer "what do we OWE this operator", and they FX-normalise it
 *     into the operator's settlement currency to do so.
 *   - `getOperatorLines`' consumers — `computeAnalyticsStats` and
 *     `getEmployeePerformance` — exclude them, because they answer "what did
 *     the trip cost to sell".
 *
 * This row is the second kind. It is labelled "Price": the fee the operators
 * were ASSIGNED, which is what `resolved_cost.price` resolves. Adding
 * adjustments would turn it into a partial, unconverted version of the
 * "owed" figure — close enough to Month End's to be compared against it,
 * never equal to it, because this panel applies no FX. A number that looks
 * like another screen's and silently is not is precisely the failure this
 * whole ticket was.
 *
 * Nothing is hidden by the choice: each operator's adjustments are shown on
 * its own card immediately above, per currency.
 *
 * If someone later decides this total SHOULD include adjustments: it is not
 * a one-line change. Rename the row (it is no longer a "price"), decide what
 * it does about FX — and if the intent is to match Month End, the honest fix
 * is to read that screen's backend-computed
 * `operator_balance_in_settlement` rather than re-deriving a total here.
 *
 * No FX is applied, deliberately. The two aggregators above this have
 * DIFFERENT, documented FX anchors (trade-date for the ledger view,
 * period-end for Month End) and converting here would mean picking one and
 * disagreeing with the other on screen. A panel showing a booking's own
 * figures has no reason to convert at all.
 *
 * Entries with no resolved price contribute nothing — they are not zeroes.
 *
 * @returns {Array<{currency: string|null, amount: number}>} one row per
 *   currency, in first-seen order. Empty when nothing resolved.
 */
export function operatorPriceTotals(operators) {
  // `reduce` over a Map rather than a loop: this repo's eslint forbids
  // `for…of` (no-restricted-syntax, regenerator-runtime) — unlike the API's,
  // which is why the helper this mirrors is written the other way.
  const byCurrency = (operators || []).reduce((acc, op) => {
    const resolved = operatorEntryPrice(op);
    if (resolved) acc.set(resolved.currency, (acc.get(resolved.currency) || 0) + resolved.amount);

    return acc;
  }, new Map());

  return Array.from(byCurrency.entries()).map(([currency, amount]) => ({ currency, amount }));
}

/**
 * The per-currency totals as one line — "6,000 HUF · 40 EUR".
 *
 * Null when nothing resolved, so the caller can drop the row rather than
 * print a bare "0" next to a currency it had to guess. The separator matches
 * the Orders table's operator-price column.
 */
export function formatOperatorPriceTotals(operators) {
  const rows = operatorPriceTotals(operators);
  if (rows.length === 0) return null;

  return rows
    .map(({ currency, amount }) => [thousandSeparator(amount), currency].filter(Boolean).join(' '))
    .join(' · ');
}
